import React, { useState, useMemo, useEffect, useRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';

function CustomCalendarPicker({
  value,
  startDate,
  endDate,
  isRange,
  onChange,
  disabled,
  placeholder,
  showFilters = false,
  filterType = 'actual',
  filterBy = 'exact',
  filterByOptions = [{ value: 'exact', label: 'Exact' }],
  onFilterTypeChange,
  onFilterByChange,
  onResetAll,
  onApplyFilters,
}) {
  const DATE_FORMAT = 'DD/MM/YYYY';
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (isRange && startDate) {
      return moment(startDate);
    }
    if (!isRange && value) {
      return moment(value);
    }
    return moment();
  });

  const [tempStartDate, setTempStartDate] = useState(startDate ? moment(startDate) : null);
  const [tempEndDate, setTempEndDate] = useState(endDate ? moment(endDate) : null);
  const [tempSingleDate, setTempSingleDate] = useState(value ? moment(value) : null);
  const [isDateSet, setIsDateSet] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [localFilterType, setLocalFilterType] = useState(filterType);
  const [localFilterBy, setLocalFilterBy] = useState(filterBy);
  const calendarRef = useRef(null);

  const monthStart = currentMonth.clone().startOf('month');
  const monthEnd = currentMonth.clone().endOf('month');
  const startDateCalendar = monthStart.clone().startOf('week');
  const endDateCalendar = monthEnd.clone().endOf('week');

  const calendarDays = useMemo(() => {
    const days = [];
    const day = startDateCalendar.clone();
    while (day.isSameOrBefore(endDateCalendar, 'day')) {
      days.push(day.clone());
      day.add(1, 'day');
    }
    return days;
  }, [startDateCalendar, endDateCalendar]);

  const handleDateClick = (day) => {
    if (disabled) return;

    setIsDateSet(false);

    if (!isRange) {
      setTempSingleDate(day);
      return;
    }

    if (!tempStartDate || tempEndDate) {
      setTempStartDate(day);
      setTempEndDate(null);
      return;
    }

    if (day.isBefore(tempStartDate)) {
      setTempEndDate(tempStartDate);
      setTempStartDate(day);
    } else {
      setTempEndDate(day);
    }
  };

  const handleSetDate = () => {
    if (isRange) {
      if (!tempStartDate || !tempEndDate) return;
      onChange([tempStartDate.toDate(), tempEndDate.toDate()]);
      setIsDateSet(true);
      setIsCalendarOpen(false);
      return;
    }

    if (!tempSingleDate) return;
    onChange(tempSingleDate.toDate());
    setIsDateSet(true);
    setIsCalendarOpen(false);
  };

  const isDateSelected = (day) => {
    if (isRange) {
      if (tempStartDate && tempEndDate) {
        return day.isSameOrAfter(tempStartDate, 'day') && day.isSameOrBefore(tempEndDate, 'day');
      }
      if (tempStartDate) {
        return day.isSame(tempStartDate, 'day');
      }
      return false;
    }

    return tempSingleDate ? day.isSame(tempSingleDate, 'day') : false;
  };

  const isDateInRange = (day) => {
    if (!isRange || !tempStartDate || !tempEndDate) return false;
    return day.isAfter(tempStartDate, 'day') && day.isBefore(tempEndDate, 'day');
  };

  const isStartDate = (day) => {
    if (!isRange || !tempStartDate) return false;
    return day.isSame(tempStartDate, 'day');
  };

  const isEndDate = (day) => {
    if (!isRange || !tempEndDate) return false;
    return day.isSame(tempEndDate, 'day');
  };

  const getDisplayValue = () => {
    if (isRange) {
      if (tempStartDate && tempEndDate) {
        return `${tempStartDate.format(DATE_FORMAT)} to ${tempEndDate.format(DATE_FORMAT)}`;
      }
      if (tempStartDate) {
        return tempStartDate.format(DATE_FORMAT);
      }
      return '';
    }

    return tempSingleDate ? tempSingleDate.format(DATE_FORMAT) : '';
  };

  const canSetDate = () => {
    if (isRange) {
      return tempStartDate && tempEndDate;
    }

    return tempSingleDate !== null;
  };

  const previousMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
  };

  const nextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, 'month'));
  };

  const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  const handleInputClick = () => {
    if (!disabled) {
      setIsCalendarOpen(!isCalendarOpen);
      if (!isCalendarOpen) {
        setIsDateSet(false);
      }
    }
  };

  const handleFilterTypeChange = (type) => {
    setLocalFilterType(type);
    if (onFilterTypeChange) {
      onFilterTypeChange(type);
    }
  };

  const handleFilterByChange = (e) => {
    const selectedValue = e.target.value;
    setLocalFilterBy(selectedValue);
    if (onFilterByChange) {
      onFilterByChange(selectedValue);
    }
  };

  const handleResetAll = () => {
    setTempStartDate(startDate ? moment(startDate) : null);
    setTempEndDate(endDate ? moment(endDate) : null);
    setTempSingleDate(value ? moment(value) : null);
    setIsDateSet(false);
    setLocalFilterType('actual');
    setLocalFilterBy('exact');
    if (onResetAll) {
      onResetAll();
    }
  };

  const handleApplyFilters = () => {
    if (onApplyFilters) {
      onApplyFilters({
        filterType: localFilterType,
        filterBy: localFilterBy,
        date: isRange ? [tempStartDate?.toDate(), tempEndDate?.toDate()] : tempSingleDate?.toDate(),
      });
    }
    setIsCalendarOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };

    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  return (
    <div className="custom-calendar-picker" ref={calendarRef}>
      <div className="calendar-input-container">
        <input
          type="text"
          className="form-control calendar-input"
          value={getDisplayValue()}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          onClick={handleInputClick}
        />
        <i className="icon-Calender calendar-icon" aria-hidden="true" />
      </div>
      {isCalendarOpen && (
        <div className="calendar-container">
          {showFilters && (
            <div className="calendar-filters">
              <div className="filter-type-group">
                <label htmlFor="filter-type-actual" className="filter-radio-label">
                  <input
                    id="filter-type-actual"
                    type="radio"
                    name="filterType"
                    value="actual"
                    checked={localFilterType === 'actual'}
                    onChange={() => handleFilterTypeChange('actual')}
                    disabled={disabled}
                  />
                  <span className="filter-radio-text">Actual</span>
                </label>
                <label htmlFor="filter-type-planned" className="filter-radio-label">
                  <input
                    id="filter-type-planned"
                    type="radio"
                    name="filterType"
                    value="planned"
                    checked={localFilterType === 'planned'}
                    onChange={() => handleFilterTypeChange('planned')}
                    disabled={disabled}
                  />
                  <span className="filter-radio-text">Planned</span>
                </label>
              </div>
              <div className="filter-by-group">
                <label htmlFor="filter-by-select" className="filter-by-label">
                  Filter by:
                </label>
                <select
                  id="filter-by-select"
                  className="form-control filter-by-select"
                  value={localFilterBy}
                  onChange={handleFilterByChange}
                  disabled={disabled}
                >
                  {filterByOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="select-date-group">
                <label htmlFor="select-date-input" className="select-date-label">
                  Select Date:
                </label>
                <div className="select-date-input-wrapper">
                  <input id="select-date-input" type="text" className="form-control select-date-input" value={getDisplayValue()} readOnly disabled={disabled} />
                  <i className="icon-Calender calendar-icon-small" aria-hidden="true" />
                </div>
              </div>
            </div>
          )}
          <div className="calendar-header">
            <button type="button" className="calendar-nav-btn" onClick={previousMonth} disabled={disabled} aria-label="Previous month">
              <span className="icon-new-down-arrow-icon icon-inverse f-14 calendar-nav-icon calendar-nav-icon-prev" />
            </button>
            <div className="calendar-month-year">
              <div className="calendar-month">{currentMonth.format('MMMM')}</div>
              <div className="calendar-year">{currentMonth.format('YYYY')}</div>
            </div>
            <button type="button" className="calendar-nav-btn" onClick={nextMonth} disabled={disabled} aria-label="Next month">
              <span className="icon-new-down-arrow-icon f-14 calendar-nav-icon calendar-nav-icon-next" />
            </button>
          </div>
          <div className="calendar-weekdays">
            {weekDays.map((day) => (
              <div key={day} className="calendar-weekday">
                {day}
              </div>
            ))}
          </div>
          <div className="calendar-days">
            {calendarDays.map((day) => {
              const isCurrentMonth = day.isSame(currentMonth, 'month');
              const isSelected = isDateSelected(day);
              const inRange = isDateInRange(day);
              const isStart = isStartDate(day);
              const isEnd = isEndDate(day);

              return (
                <button
                  key={day.format('YYYY-MM-DD')}
                  type="button"
                  className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''} ${inRange ? 'in-range' : ''} ${
                    isStart ? 'start-date' : ''
                  } ${isEnd ? 'end-date' : ''}`}
                  onClick={() => handleDateClick(day)}
                  disabled={disabled || !isCurrentMonth}
                >
                  {day.format('D')}
                </button>
              );
            })}
          </div>
          <div className="calendar-actions">
            <button
              type="button"
              className={`btn btn-primary btn-set-date ${isDateSet ? 'btn-set-date-active' : ''}`}
              onClick={handleSetDate}
              disabled={disabled || !canSetDate()}
            >
              Set Date
            </button>
          </div>
          {showFilters && (
            <div className="calendar-filter-actions">
              <button type="button" className="btn btn-secondary btn-reset-all" onClick={handleResetAll} disabled={disabled}>
                Reset All
              </button>
              <button type="button" className="btn btn-primary btn-apply-filters" onClick={handleApplyFilters} disabled={disabled}>
                Apply Filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

CustomCalendarPicker.propTypes = {
  value: PropTypes.instanceOf(Date),
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  isRange: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  showFilters: PropTypes.bool,
  filterType: PropTypes.oneOf(['actual', 'planned']),
  filterBy: PropTypes.string,
  filterByOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  onFilterTypeChange: PropTypes.func,
  onFilterByChange: PropTypes.func,
  onResetAll: PropTypes.func,
  onApplyFilters: PropTypes.func,
};

CustomCalendarPicker.defaultProps = {
  value: null,
  startDate: null,
  endDate: null,
  isRange: false,
  disabled: false,
  placeholder: 'Select Date',
  showFilters: false,
  filterType: 'actual',
  filterBy: 'exact',
  filterByOptions: [{ value: 'exact', label: 'Exact' }],
  onFilterTypeChange: null,
  onFilterByChange: null,
  onResetAll: null,
  onApplyFilters: null,
};

export default CustomCalendarPicker;
